<template>
  <div class="anamnesis-container">
    <Breadcrumbs />
  </div>

  <div class="relative min-h-screen bg-gray-50">
    <!-- Barre d'outils -->
    <div class="anamnesis-container mb-4">
      <div class="bg-white rounded-lg shadow-sm p-4">
        <!-- Titre et Type -->
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-4">
            <component :is="getTypeIcon" v-if="getTypeIcon" class="h-5 w-5 text-gray-600" />
            <div class="flex flex-col items-start">
              <div class="relative">
                <input v-if="isEditingTitle" ref="titleInput" v-model="editedTitle" type="text"
                  class="text-xl font-semibold w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  @blur="handleTitleUpdate" @keyup.enter="handleTitleUpdate" />
                <h1 v-else class="text-xl font-semibold px-2 py-1 rounded"
                  :class="canEditMemo ? 'cursor-pointer hover:bg-gray-50' : 'cursor-default'"
                  @click="canEditMemo && startTitleEdit()">
                  {{ memo?.title || 'Sans titre' }}
                </h1>
              </div>
              <p v-if="memo?.triid" class="text-xs text-gray-500 ml-2">{{ memo.triid }}</p>
            </div>
          </div>

          <div class="flex items-center gap-4">
            <!-- Bulle du propriétaire -->
            <div class="px-2 py-1 rounded-full text-xs flex items-center gap-1 bg-blue-100 text-blue-700 mr-4">
              <UserIcon class="h-3.5 w-3.5" />
              <span>Propriétaire: {{ ownerName || 'Chargement...' }}</span>
            </div>
            
            <!-- Bulles de statut -->
            <div class="flex items-center gap-2 mr-4">
              <button class="px-2 py-1 rounded-full text-xs flex items-center gap-1 transition-colors duration-200"
                :class="[
                  memo?.is_model ? 'bg-purple-100 text-purple-700' : 'bg-pink-100 text-pink-700',
                  canEditMemo ? 'cursor-pointer hover:bg-opacity-80' : 'cursor-default'
                ]" @click="canEditMemo && handleModelToggle()">
                <BookTemplateIcon v-if="memo?.is_model" class="h-3.5 w-3.5" />
                <FileIcon v-else class="h-3.5 w-3.5" />
                <span>{{ memo?.is_model ? 'Modèle' : 'Fiche' }}</span>
              </button>
              <button class="px-2 py-1 rounded-full text-xs flex items-center gap-1 transition-colors duration-200"
                :class="[
                  memo?.is_draft ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700',
                  canEditMemo ? 'cursor-pointer hover:bg-opacity-80' : 'cursor-default'
                ]" @click="canEditMemo && handleDraftToggle()">
                <FileEditIcon v-if="memo?.is_draft" class="h-3.5 w-3.5" />
                <FileCheckIcon v-else class="h-3.5 w-3.5" />
                <span>{{ memo?.is_draft ? 'Brouillon' : 'Validé' }}</span>
              </button>
              <button class="px-2 py-1 rounded-full text-xs flex items-center gap-1 transition-colors duration-200"
                :class="[
                  memo?.is_public ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700',
                  canEditMemo ? 'cursor-pointer hover:bg-opacity-80' : 'cursor-default'
                ]" @click="canEditMemo && handlePublicToggle()">
                <EyeIcon v-if="memo?.is_public" class="h-3.5 w-3.5" />
                <EyeOffIcon v-else class="h-3.5 w-3.5" />
                <span>{{ memo?.is_public ? 'Public' : 'Privé' }}</span>
              </button>
            </div>
            <!-- Séparateur visuel -->
            <div class="border-l border-gray-200 h-8 mx-2"></div>
            <!-- Bouton éditer à droite des bulles -->
            <div class="flex items-center gap-2 mr-4">
              <button v-if="!memo?.is_model && canEditMemo"
                class="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors duration-200 flex items-center gap-1"
                title="Éditer" @click="handleEdit">
                <EditIcon class="h-5 w-5" />
                <span class="text-sm">Éditer</span>
              </button>
              <button v-if="!canEditMemo"
                class="p-2 text-orange-600 hover:text-orange-900 hover:bg-orange-100 rounded-lg transition-colors duration-200 flex items-center gap-1"
                title="Voir en lecture seule" @click="handleViewReadOnly">
                <EyeIcon class="h-5 w-5" />
                <span class="text-sm">Voir</span>
              </button>
              <template v-else-if="memo?.is_model">
                <button v-if="canEditMemo"
                  class="p-2 text-purple-600 hover:text-purple-900 hover:bg-purple-100 rounded-lg transition-colors duration-200 flex items-center gap-1"
                  title="Éditer le modèle" @click="handleEditModel">
                  <EditIcon class="h-5 w-5" />
                  <span class="text-sm">Éditer le modèle</span>
                </button>
                <button
                  class="p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-100 rounded-lg transition-colors duration-200 flex items-center gap-1"
                  title="Utiliser comme modèle" @click="handleUseModel">
                  <CopyIcon class="h-5 w-5" />
                  <span class="text-sm">Utiliser comme modèle</span>
                </button>
              </template>
            </div>
            <!-- Actions -->
            <div class="flex items-center gap-2">
              <!-- Dropdown export -->
              <div ref="exportDropdownRef" class="relative">
                <button
                  class="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors duration-200 flex items-center gap-1"
                  title="Exporter"
                  @click="exportDropdownOpen = !exportDropdownOpen"
                >
                  <DownloadIcon class="h-5 w-5" />
                  <span class="ml-1 text-sm font-medium">Exporter</span>
                </button>
                <div v-if="exportDropdownOpen" class="absolute right-0 z-50 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg py-2">
                  <div class="px-4 py-2 text-xs text-gray-500 font-semibold">Format</div>
                  <button class="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm" @click="memo && generateRawJson(memo)">
                    Anamnesis
                  </button>
                  <button class="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm" @click="memo && generateJson(memo)">
                    MEMoS (actuel)
                  </button>
                  <hr class="my-2" />
                  <div class="px-4 py-2 text-xs text-gray-500 font-semibold">QR Code</div>
                  <button class="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm" @click="memo && generateQRCodeUUID(memo.uuid)">
                    QR Code UUID
                  </button>
                  <hr class="my-2" />
                  <div class="px-4 py-2 text-xs text-gray-500 font-semibold">Mappings disponibles</div>
                  <template v-if="exportMappingsStore.activeMappings.length > 0">
                    <div v-for="mapping in exportMappingsStore.activeMappings" :key="mapping.uuid"
                      class="w-full px-4 py-2 hover:bg-gray-50 text-sm flex justify-between items-center">
                      <span>{{ mapping.name }}</span>
                      <div class="ml-2 flex items-center gap-2">
                        <template v-if="isQrCodeOptimizedMapping(mapping)">
                          <button
                            class="p-1.5 hover:bg-gray-200 rounded transition-colors"
                            title="Télécharger en QR Code"
                            @click.stop="memo && handleExportMapping(mapping.uuid, 'qr_code')">
                            <QrCodeIcon class="h-4 w-4 text-gray-600" />
                          </button>
                          <button
                            class="p-1.5 hover:bg-gray-200 rounded transition-colors"
                            title="Télécharger en JSON"
                            @click.stop="memo && handleExportMapping(mapping.uuid, 'json')">
                            <FileIcon class="h-4 w-4 text-gray-600" />
                          </button>
                        </template>
                        <template v-else>
                          <button
                            class="p-1.5 hover:bg-gray-200 rounded transition-colors"
                            :title="`Télécharger en ${mapping?.format_type?.toUpperCase() || ''}`"
                            @click.stop="memo && handleExportMapping(mapping.uuid, mapping.format_type)">
                            <FileIcon class="h-4 w-4 text-gray-600" />
                          </button>
                        </template>
                      </div>
                    </div>
                  </template>
                  <div v-else class="px-4 py-2 text-xs text-gray-400">Aucun mapping actif</div>
                </div>
              </div>
              <button v-if="canDeleteMemo"
                class="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors duration-200"
                title="Supprimer" @click="handleDelete">
                <TrashIcon class="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>



    <div class="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div class="flex">
        <!-- Navigation verticale -->
        <nav class="hidden md:block w-64 sticky top-50 self-start h-[calc(100vh-5rem)] overflow-y-auto">
          <div class="sticky top-4 bg-white rounded-lg shadow-sm p-4">
            <ul class="space-y-2">
              <li v-for="section in sections" v-show="section.id !== 'images' || hasImages || imagesLoading" :key="section.id">
                <a :href="'#' + section.id"
                  class="block px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                  :class="{ 'bg-blue-50 text-blue-700': currentSection === section.id }"
                  @click.prevent="scrollToSection(section.id)">
                  <component :is="section.icon" class="h-4 w-4 inline mr-2" />
                  {{ section.name }}
                </a>
              </li>
            </ul>
          </div>
        </nav>

        <!-- Contenu des sections -->
        <div class="flex-1 min-w-0 ml-0 md:ml-8">
          <!-- ACTIVITY -->
          <section id="activity" class="mb-8">
            <h2 class="text-lg font-medium mb-4 flex items-center gap-2 text-gray-600">
              <LibraryBigIcon class="h-5 w-5 text-gray-500" />
              ACTIVITY
            </h2>
            <div v-if="memo?.schema_model" class="bg-white rounded-lg shadow-sm p-4">
              <div class="space-y-2">
                <div class="flex items-center gap-2 text-sm text-gray-600">
                  <component :is="getTypeIcon" class="h-4 w-4" />
                  <h3 class="text-lg font-medium text-gray-900">{{ memo.schema_model.type }}</h3>
                  <span>{{ memo.schema_model.appellation }}</span>
                </div>
                <p class="text-sm text-gray-600 mt-2">
                  {{ memo.schema_model.description }}
                </p>
              </div>
            </div>
          </section>

          <!-- IMAGES -->
          <section v-if="hasImages || imagesLoading" id="images" class="mb-8">
            <h2 class="text-lg font-medium mb-4 flex items-center gap-2 text-gray-600">
              <ImageIcon class="h-5 w-5 text-gray-500" />
              IMAGES
            </h2>
            <div class="bg-white rounded-lg shadow-sm p-4">
              <ImageDisplay
                :images="images"
                :loading="imagesLoading"
                :error="imagesError"
                :can-delete="canEditMemo"
                :get-image-url="getImageUrl"
                @delete="deleteImage"
              />
            </div>
          </section>

          <!-- WHAT -->
          <section id="what" class="mb-8">
            <h2 class="text-lg font-medium mb-4 flex items-center gap-2 text-gray-600">
              <BoxIcon class="h-5 w-5 text-gray-500" />
              WHAT
            </h2>
            <div class="space-y-4">
              <MaterialEntityCard :material-entity="memo?.material_entity" :view-mode="'memostable'"
                :show-actions="false" />
              <DigitalEntityCard v-if="showDigitalEntityInput" :digital-entity="memo?.digital_entity_input"
                :is-input="true" :view-mode="'memostable'" :show-actions="false" />
              <DigitalEntityCard :digital-entity="memo?.digital_entity_output" :is-input="false"
                :view-mode="'memostable'" :show-actions="false" />
            </div>
          </section>

          <!-- WHY -->
          <section id="why" class="mb-8">
            <h2 class="text-lg font-medium mb-4 flex items-center gap-2 text-gray-600">
              <HelpCircleIcon class="h-5 w-5 text-gray-500" />
              WHY
            </h2>
            <ProjectCard :project="memo?.project" :instrumentation-description="memo?.instrumentation_description || ''"
              :instrumentation-link="memo?.instrumentation_link || ''" :view-mode="'memostable'"
              :show-actions="false" />
          </section>

          <!-- WHO -->
          <section id="who" class="mb-8">
            <h2 class="text-lg font-medium mb-4 flex items-center gap-2 text-gray-600">
              <UsersIcon class="h-5 w-5 text-gray-500" />
              WHO
            </h2>
            <div class="space-y-4">
              <template v-if="memo?.agents_entries?.length && memo?.agents_entries?.length > 0">
                <AgentCard v-for="agentEntry in memo.agents_entries" :key="agentEntry.agent.uuid"
                  :agent="{ ...agentEntry.agent, memos: [] }" :roles="agentEntry.roleslist" :view-mode="'memostable'"
                  :show-actions="false" />
              </template>
              <AgentCard v-else :agent="null" :roles="''" :view-mode="'memostable'" :show-actions="false" />
            </div>
          </section>

          <!-- WHERE -->
          <section id="where" class="mb-8">
            <h2 class="text-lg font-medium mb-4 flex items-center gap-2 text-gray-600">
              <MapPinIcon class="h-5 w-5 text-gray-500" />
              WHERE
            </h2>
            <LocationCard :location="memo?.location" :view-mode="'memostable'" :show-actions="false" />
          </section>

          <!-- WHEN -->
          <section id="when" class="mb-8">
            <h2 class="text-lg font-medium mb-4 flex items-center gap-2 text-gray-600">
              <ClockIcon class="h-5 w-5 text-gray-500" />
              WHEN
            </h2>
            <div class="bg-white rounded-lg shadow-sm p-4">
              <template v-if="memo?.start_time">
                <div class="space-y-4">
                  <div class="flex items-center gap-2">
                    <CalendarClockIcon class="h-4 w-4 text-gray-600" />
                    <span v-if="memo?.end_time" class="text-sm font-medium text-gray-700">Début</span>
                    <p class="text-gray-800">{{ formatDateTime(memo?.start_time) }}</p>
                  </div>

                  <div v-if="memo?.end_time" class="flex items-center gap-2">
                    <CalendarCheckIcon class="h-4 w-4 text-gray-600" />
                    <span class="text-sm font-medium text-gray-700">Fin</span>
                    <p class="text-gray-800">{{ formatDateTime(memo?.end_time) }}</p>
                  </div>

                  <div v-if="memo?.time_zone" class="flex items-center gap-2 text-sm text-gray-600">
                    <ClockIcon class="h-4 w-4" />
                    <span>{{ memo.time_zone }}</span>
                  </div>
                </div>
              </template>
              <div v-else class="text-gray-500 text-sm flex items-center gap-2">
                <CalendarClockIcon class="h-4 w-4 text-gray-400" />
                <span>Aucune date définie</span>
              </div>
            </div>
          </section>

          <!-- HOW | WHICH -->
          <section id="how" class="mb-8">
            <h2 class="text-lg font-medium mb-4 flex items-center gap-2 text-gray-600">
              <WrenchIcon class="h-5 w-5 text-gray-500" />
              HOW | WHICH
            </h2>
            <div class="bg-white rounded-lg shadow-sm p-4">
              <template v-if="memo?.data_entries?.length">
                <div class="space-y-6">
                  <template v-for="(group, section) in groupedDataEntries" :key="section">
                    <div class="border-t border-gray-200 pt-4 first:border-0 first:pt-0">
                      <h4 class="text-sm font-medium text-gray-500 mb-2">{{ section }}</h4>
                      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div v-for="entry in group" :key="entry.name" class="flex items-start">
                          <span class="text-sm text-gray-900">{{ entry.name?.split('.').pop() || '' }}:</span>
                          <span class="ml-2 text-sm text-gray-600">{{ entry.value }}</span>
                        </div>
                      </div>
                    </div>
                  </template>
                </div>
              </template>
              <div v-else class="text-gray-500 text-sm flex items-center gap-2">
                <WrenchIcon class="h-4 w-4 text-gray-400" />
                <span>Aucune donnée disponible</span>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>

    <!-- Bouton de retour en haut -->
    <ScrollToTop />

    <Notification ref="notification" />
    <ModalAreYouSure v-model="showDeleteModal" title="Supprimer le mémo ?"
      message="Cette action est irréversible. Êtes-vous sûr de vouloir supprimer ce mémo ?" confirm-text="Supprimer"
      cancel-text="Annuler" @confirm="confirmDelete" />
    <QRCodeSectionSelector
      v-model="showQRCodeSelector"
      :json-data="qrCodeJsonData"
      :max-size="2953"
      @confirm="handleQRCodeConfirm"
    />
  </div>
</template>

<script setup lang="ts">
import {
  BookTemplate as BookTemplateIcon,
  Box as BoxIcon,
  CalendarCheck as CalendarCheckIcon,
  CalendarClock as CalendarClockIcon,
  Camera as CameraIcon,
  CircleEllipsis as CircleEllipsisIcon,
  Clock as ClockIcon,
  Cog as CogIcon,
  Copy as CopyIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Eye as EyeIcon,
  EyeOff as EyeOffIcon,
  FileCheck as FileCheckIcon,
  FileEdit as FileEditIcon,
  File as FileIcon,
  HelpCircle as HelpCircleIcon,
  Image as ImageIcon,
  LibraryBig as LibraryBigIcon,
  MapPin as MapPinIcon,
  PenTool as PenToolIcon,
  QrCode as QrCodeIcon,
  Trash2 as TrashIcon,
  User as UserIcon,
  Users as UsersIcon,
  Wrench as WrenchIcon
} from 'lucide-vue-next';
import QRCode from 'qrcode';
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { memos } from '../../api';
import { updateMemo } from '../../api/memos';
import type { Memo, MemoCreate } from '../../api/types/memos/types';
import { useMappings } from '../../composables/map/useMappings';
import { useGenerators } from '../../composables/useGenerators';
import { useMemoImages } from '../../composables/useMemoImages';

import { useAssetsCacheStore } from '../../stores/assetsCache';
import { useAuthStore } from '../../stores/auth';
import { useBreadcrumbsStore } from '../../stores/breadcrumbs';
import { useExportMappingsStore } from '../../stores/exportMappings';
import { useUserNamesCacheStore } from '../../stores/userNamesCache';
import Breadcrumbs from '../Breadcrumbs.vue';
import Notification from '../Notification.vue';
import AgentCard from '../cards/AgentCard.vue';
import DigitalEntityCard from '../cards/DigitalEntityCard.vue';
import LocationCard from '../cards/LocationCard.vue';
import MaterialEntityCard from '../cards/MaterialEntityCard.vue';
import ProjectCard from '../cards/ProjectCard.vue';
import ImageDisplay from '../common/ImageDisplay.vue';
import ScrollToTop from '../utils/ScrollToTop.vue';
import ModalAreYouSure from '../utils/modal_areyousure.vue';
import QRCodeSectionSelector from './QRCodeSectionSelector.vue';

interface Props {
  uuid: string;
}

const props = defineProps<Props>();
    const route = useRoute();
    const router = useRouter();
    const breadcrumbsStore = useBreadcrumbsStore();
const authStore = useAuthStore();
const notification = ref<InstanceType<typeof Notification> | null>(null);
const assetsCacheStore = useAssetsCacheStore();
const userNamesCacheStore = useUserNamesCacheStore();
    const { generateJson, generateRawJson, generateQRCodeUUID } = useGenerators();
    const exportMappingsStore = useExportMappingsStore();
    const exportDropdownOpen = ref(false);
    const exportDropdownRef = ref<HTMLElement | null>(null);
    const { downloadFile, applyExportMapping, isLoadingRaw, errorRaw } = useMappings();
    const showQRCodeSelector = ref(false);
    const qrCodeJsonData = ref<Record<string, any>>({});
    const pendingQRCodeMapping = ref<{ mappingUuid: string; filename: string } | null>(null);

    // Gestion des images du mémo
    const { images, loading: imagesLoading, error: imagesError, deleteImage, getImageUrl, hasImages } = useMemoImages(props.uuid);

    // État du mémo
    const memo = ref<Memo | null>(window.history.state?.memo || null);
    
    // Log de débogage
    if (window.history.state?.memo) {
      console.log('🔍 [MEMO_DETAIL] Données reçues via window.history.state:', window.history.state.memo);
    } else {
      console.log('🔍 [MEMO_DETAIL] Aucune donnée dans window.history.state, API call requis');
    }
    const currentSection = ref('what');
    const showDeleteModal = ref(false);
    const isEditingTitle = ref(false);
const editedTitle = ref('');
const titleInput = ref<HTMLInputElement | null>(null);
const ownerName = ref<string>('');

    // Sections de navigation
    const sections = [
      { id: 'activity', name: 'ACTIVITY', icon: LibraryBigIcon },
      { id: 'images', name: 'IMAGE', icon: ImageIcon },
      { id: 'what', name: 'WHAT', icon: BoxIcon },
      { id: 'why', name: 'WHY', icon: HelpCircleIcon },
      { id: 'who', name: 'WHO', icon: UsersIcon },
      { id: 'where', name: 'WHERE', icon: MapPinIcon },
      { id: 'when', name: 'WHEN', icon: ClockIcon },
      { id: 'how', name: 'HOW | WHICH', icon: WrenchIcon }
    ];

    // Computed pour l'icône du type
    const getTypeIcon = computed(() => {
      const type = memo.value?.schema_model?.type?.toLowerCase();
      switch (type) {
        case 'acquisition':
          return CameraIcon;
        case 'processing':
          return CogIcon;
        case 'analyse':
          return PenToolIcon;
        case 'autre':
          return CircleEllipsisIcon;
        default:
          return null;
      }
    });

    // Computed pour afficher ou non le digital entity input
    const showDigitalEntityInput = computed(() => {
      return memo.value?.schema_model?.type?.toLowerCase() !== 'acquisition';
    });

    // Groupement des données
    const groupedDataEntries = computed(() => {
      if (!memo.value?.data_entries) return {};

      return memo.value.data_entries.reduce((acc, entry) => {
        const section = entry.name?.split('.')[0] || 'other';
        if (!acc[section]) acc[section] = [];
        acc[section].push(entry);
        return acc;
      }, {} as Record<string, typeof memo.value.data_entries>);
    });

    // Computed pour les permissions
    const canEditMemo = computed((): boolean => {
      return memo.value ? authStore.canEditAsset(memo.value as unknown as { user_id: string }) : false;
    });

    const canDeleteMemo = computed((): boolean => {
      return memo.value ? authStore.canDeleteAsset(memo.value as unknown as { user_id: string }) : false;
    });

    // Chargement initial
    onMounted(async () => {
      if (!memo.value && props.uuid) {
        try {
          let response;
          // Si l'utilisateur est connecté, utiliser l'API normale
          if (authStore.isAuthenticated) {
            console.log('🔍 [MEMO_DETAIL] Appel API fetchMemosUuid pour UUID:', props.uuid);
            response = await memos.fetchMemosUuid(props.uuid);
            console.log('🔍 [MEMO_DETAIL] Réponse API fetchMemosUuid:', response);
            if (response && response.length > 0) {
              memo.value = response[0];
              console.log('🔍 [MEMO_DETAIL] Memo chargé depuis API:', memo.value);
            }
          } else {
            console.log('🔍 [MEMO_DETAIL] Appel API getPublicByUuid pour UUID:', props.uuid);
            // Sinon, utiliser l'API publique qui permet l'accès par UUID
            response = await memos.getPublicByUuid(props.uuid);
            console.log('🔍 [MEMO_DETAIL] Réponse API getPublicByUuid:', response);
            memo.value = response;
          }
          
          if (memo.value) {
            const memoTitle = memo.value.title || memo.value.triid || 'Sans titre';
            breadcrumbsStore.updateCrumbName(route.fullPath, memoTitle);
            await loadOwnerName();
          } else {
            console.error('Mémo non trouvé');
            if (authStore.isAuthenticated) {
              router.push('/gestion-memos');
            } else {
              router.push('/');
            }
          }
        } catch (error) {
          console.error('Erreur lors de la récupération du mémo:', error);
          if (authStore.isAuthenticated) {
            router.push('/gestion-memos');
          } else {
            router.push('/');
          }
        }
      } else if (memo.value) {
        const memoTitle = memo.value.title || memo.value.triid || 'Sans titre';
        breadcrumbsStore.updateCrumbName(route.fullPath, memoTitle);
        await loadOwnerName();
      }
      
      // Charger les images du mémo
      // await fetchImages();
      
      if (exportMappingsStore.activeMappings.length === 0) {
        await exportMappingsStore.loadMappings();
      }
    });

    // Fermer le dropdown si clic en dehors
    const handleClickOutside = (event: MouseEvent): void => {
      if (exportDropdownRef.value && !exportDropdownRef.value.contains(event.target as Node)) {
        exportDropdownOpen.value = false;
      }
    };
    onMounted(() => {
      document.addEventListener('mousedown', handleClickOutside);
    });
    onUnmounted(() => {
      document.removeEventListener('mousedown', handleClickOutside);
    });

    // Export via mapping (local, pas backend)
    const isQrCodeOptimizedMapping = (mapping: any): boolean => {
      return (
        mapping?.format_type === 'json' &&
        Array.isArray(mapping?.export_options) &&
        mapping.export_options.some(
          (opt: any) => opt?.option_key === 'qr_code_optimized' && opt?.option_value === 'true'
        )
      );
    };

    const handleExportMapping = async (mappingUuid: string, exportFormat?: 'qr_code' | 'json' | 'csv' | 'xml'): Promise<void> => {
      if (!memo.value) return;
      const mapping = exportMappingsStore.getMappingByUuid(mappingUuid);
      if (!mapping) return;
      try {
        isLoadingRaw.value = true;
        errorRaw.value = null;
        
        // Déterminer le format d'export
        let targetFormat: 'json' | 'xml' | 'csv' = mapping.format_type as 'json' | 'xml' | 'csv';
        if (exportFormat && exportFormat !== 'qr_code') {
          targetFormat = exportFormat as 'json' | 'xml' | 'csv';
        }
        
        // Utiliser la logique unifiée de preview/export
        const result = await applyExportMapping(mapping, memo.value.uuid, targetFormat);
        if (!result.success) throw new Error('Erreur lors de la génération du mapping');
        const { content, format } = result;
        if (!content) throw new Error('Contenu vide');
        
        // Générer le nom de fichier selon le format demandé
        const generateMemoFilename = (memo: any, exportType: string, fileExtension: string): string => {
          const sanitizeFilename = (str: string): string => {
            return str
              .replace(/[^a-zA-Z0-9\s]/g, '')
              .replace(/\s+/g, '_')
              .toLowerCase()
              .trim();
          };

          let baseName: string;
          
          if (memo.title && memo.title.trim()) {
            baseName = sanitizeFilename(memo.title);
          } else if (memo.triid && memo.triid.trim()) {
            const triidWords = memo.triid.split(',').map((word: string) => word.trim()).slice(0, 3);
            baseName = triidWords.join('_');
          } else {
            baseName = memo.uuid;
          }

          return `${baseName}_${exportType}.${fileExtension}`;
        };

        // Si on demande explicitement un QR code ou si c'est un mapping optimisé QR code sans format spécifié
        if (exportFormat === 'qr_code' || (isQrCodeOptimizedMapping(mapping) && !exportFormat)) {
          // Vérifier si le contenu est trop grand pour un QR code (limite ~2953 caractères pour niveau L)
          const QR_CODE_MAX_SIZE = 2953;
          
          if (content.length > QR_CODE_MAX_SIZE) {
            // Le contenu est trop grand, afficher le modal de sélection
            qrCodeJsonData.value = result.data || {};
            pendingQRCodeMapping.value = {
              mappingUuid: mappingUuid,
              filename: generateMemoFilename(memo.value, mapping.name || 'mapping', 'png')
            };
            showQRCodeSelector.value = true;
            exportDropdownOpen.value = false;
            isLoadingRaw.value = false;
            return;
          }
          
          // Le contenu est assez petit, générer directement le QR code
          const qrCodeDataUrl = await QRCode.toDataURL(content);
          const a = document.createElement('a');
          a.href = qrCodeDataUrl;
          a.download = generateMemoFilename(memo.value, mapping.name || 'mapping', 'png');
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        } else {
          // Export en fichier (JSON, CSV, XML)
          const filename = generateMemoFilename(memo.value, mapping.name || 'mapping', format.toLowerCase());
          let mimeType = 'application/json';
          if (format.toLowerCase() === 'csv') mimeType = 'text/csv';
          else if (format.toLowerCase() === 'xml') mimeType = 'application/xml';
          
          downloadFile(content, filename, mimeType);
        }

        exportDropdownOpen.value = false;
      } catch (e) {
        if (notification.value) notification.value.showNotification('Erreur export mapping', 'error');
      } finally {
        isLoadingRaw.value = false;
      }
    };

    // Formatage des dates
    const formatDateTime = (dateString: string | null | undefined): string => {
      if (!dateString) return '';
      const date = new Date(dateString);

      // Si pas de timezone, on n'affiche que la date
      if (!memo.value?.time_zone) {
        return new Intl.DateTimeFormat('fr-FR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        }).format(date);
      }

      // Si timezone définie, on affiche date et heure
      return new Intl.DateTimeFormat('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    };

    // Navigation entre sections
    const scrollToSection = (sectionId: string): void => {
      currentSection.value = sectionId;
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    };

    // Gestion de l'édition du titre
    const startTitleEdit = (): void => {
      if (!memo.value || !canEditMemo.value) return;
      editedTitle.value = memo.value.title || '';
      isEditingTitle.value = true;
      nextTick(() => {
        if (titleInput.value) {
          titleInput.value.focus();
        }
      });
    };

    // Gestion des actions
    const handleDelete = (): void => {
      if (!canDeleteMemo.value) return;
      showDeleteModal.value = true;
    };

    // Fonction utilitaire pour mettre à jour le mémo
    const updateMemoData = async (updates: Partial<MemoCreate>): Promise<boolean | undefined> => {
      if (!memo.value) return;
      try {
        const memoData: Partial<MemoCreate> = {
          user_id: memo.value.user_id,
          uuid: memo.value.uuid,
          title: memo.value.title || '',
          triid: memo.value.triid || '',
          schema_model_uuid: memo.value.schema_model.uuid,
          material_entity_uuid: memo.value.material_entity?.uuid || '',
          digital_entity_input_id: memo.value.digital_entity_input?.uuid || '',
          digital_entity_output_id: memo.value.digital_entity_output?.uuid || '',
          location_uuid: memo.value.location?.uuid || '',
          is_public: memo.value.is_public,
          is_model: memo.value.is_model,
          is_draft: memo.value.is_draft,
          data_entries: memo.value.data_entries || [],
          agent_entries: (memo.value.agents_entries || []).map(entry => ({
            agent_id: entry.agent.uuid,
            agent: entry.agent,
            roleslist: entry.roleslist
          })),
          // Ajout des champs manquants
          project_uuid: memo.value.project?.uuid || undefined,
          instrumentation_description: memo.value.instrumentation_description || '',
          instrumentation_link: memo.value.instrumentation_link || '',
          start_time: memo.value.start_time || undefined,
          end_time: memo.value.end_time || undefined,
          time_zone: memo.value.time_zone || undefined,
          ...updates
        };

        await updateMemo(memo.value.uuid, memoData);
        // Mettre à jour l'état local avec les nouvelles valeurs
        Object.assign(memo.value, updates);
        return true;
      } catch (error) {
        console.error('Erreur lors de la mise à jour du mémo:', error);
        if (notification.value) {
          notification.value.showNotification('Erreur lors de la mise à jour du mémo', 'error');
        }
        return false;
      }
    };

    const handleTitleUpdate = async (): Promise<void> => {
      if (!memo.value || editedTitle.value === memo.value.title) {
        isEditingTitle.value = false;
        return;
      }

      const success = await updateMemoData({ title: editedTitle.value });
      if (success && notification.value) {
        notification.value.showNotification('Titre mis à jour avec succès', 'success');
      }
      isEditingTitle.value = false;
    };

    const handleModelToggle = async (): Promise<void> => {
      if (!memo.value || !canEditMemo.value) return;

      const success = await updateMemoData({ is_model: !memo.value.is_model });
      if (success && notification.value) {
        notification.value.showNotification(
          `Mémo ${memo.value.is_model ? 'défini comme modèle' : 'retiré des modèles'}`,
          'success'
        );
      }
    };

    const handlePublicToggle = async (): Promise<void> => {
      if (!memo.value || !canEditMemo.value) return;

      const success = await updateMemoData({ is_public: !memo.value.is_public });
      if (success && notification.value) {
        notification.value.showNotification(
          `Mémo ${memo.value.is_public ? 'rendu public' : 'rendu privé'}`,
          'success'
        );
      }
    };

    const handleDraftToggle = async (): Promise<void> => {
      if (!memo.value || !canEditMemo.value) return;

      const success = await updateMemoData({ is_draft: !memo.value.is_draft });
      if (success && notification.value) {
        notification.value.showNotification(
          `Mémo ${memo.value.is_draft ? 'marqué comme brouillon' : 'validé'}`,
          'success'
        );
      }
    };

    const handleEdit = (): void => {
      //console.log('MemosDetail - Editing memos with memoId:', memo.value?.uuid);
      router.push({
        path: '/form',
        query: {
          mode: 'edit',
          memoId: memo.value?.uuid,
          step: '1',
          isFromDetail: 'true'
        }
      });
    };

    const handleEditModel = (): void => {
      //console.log('MemosDetail - Editing model with memoId:', memo.value?.uuid);
      router.push({
        path: '/form',
        query: {
          mode: 'edit',
          memoId: memo.value?.uuid,
          step: '1',
          isFromDetail: 'true'
        }
      });
    };

    const handleUseModel = (): void => {
      //console.log('Using model with memoId:', memo.value?.uuid);
      router.push({
        path: '/form',
        query: {
          mode: 'useModel',
          memoId: memo.value?.uuid,
          step: '1'
        }
      });
    };

    const handleViewReadOnly = (): void => {
      //console.log('Viewing readonly memo with memoId:', memo.value?.uuid);
      router.push({
        path: '/form',
        query: {
          mode: 'edit',
          memoId: memo.value?.uuid,
          step: '1',
          isFromDetail: 'true'
        }
      });
    };

    // Intersection Observer pour suivre la section active
    onMounted(() => {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            currentSection.value = entry.target.id;
          }
        });
      }, { threshold: 0.5 });

      sections.forEach(section => {
        const element = document.getElementById(section.id);
        if (element) observer.observe(element);
      });
    });

    const confirmDelete = async (): Promise<void> => {
      if (!memo.value?.uuid) return;

      try {
        await memos.deleteMemo(memo.value.uuid);
        assetsCacheStore.removeAssetFromCache('memos', memo.value.uuid); // MAJ cache store
        router.push('/gestion-memos');
      } catch (error) {
        console.error('Erreur lors de la suppression du mémo:', error);
        if (notification.value) {
          notification.value.showNotification('Erreur lors de la suppression du mémo', 'error');
        }
      }
    };

    const loadOwnerName = async (): Promise<void> => {
      if (!memo.value?.user_id) {
        ownerName.value = 'Inconnu';
        return;
      }

      try {
        const name = await userNamesCacheStore.getUserNameAsync(memo.value.user_id);
        ownerName.value = name || 'Utilisateur privé';
      } catch (error) {
        console.error('Erreur lors du chargement du nom du propriétaire:', error);
        ownerName.value = 'Erreur de chargement';
      }
    };

    // Gérer la confirmation du modal de sélection QR code
    const handleQRCodeConfirm = async (filteredData: Record<string, any>): Promise<void> => {
      if (!pendingQRCodeMapping.value || !memo.value) return;

      try {
        isLoadingRaw.value = true;
        
        // Convertir les données filtrées en JSON compact
        const filteredContent = JSON.stringify(filteredData);
        
        // Générer le QR code avec les données filtrées
        const qrCodeDataUrl = await QRCode.toDataURL(filteredContent);
        const a = document.createElement('a');
        a.href = qrCodeDataUrl;
        a.download = pendingQRCodeMapping.value.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        if (notification.value) {
          notification.value.showNotification('QR Code généré avec succès', 'success');
        }
      } catch (error) {
        console.error('Erreur lors de la génération du QR code:', error);
        if (notification.value) {
          notification.value.showNotification('Erreur lors de la génération du QR code', 'error');
        }
      } finally {
        isLoadingRaw.value = false;
        pendingQRCodeMapping.value = null;
        qrCodeJsonData.value = {};
      }
    };


</script>

<style scoped>
/* Menu vertical */
.sticky {
  position: sticky;
  top: 2.5rem;
}

/* Transitions douces */
section {
  scroll-margin-top: 2rem;
}

/* Transitions pour le menu */
a {
  transition: all 0.2s ease-in-out;
}

/* Animation de scroll */
html {
  scroll-behavior: smooth;
}
</style>